import React, { useMemo } from 'react';
import LoadingSpinner from 'components/LoadingSpinner';
import { Button, Chip, Table, Tooltip, type TableColumn } from 'common/components';
import { AgentActivityChart } from 'pages/AgentCommandCenter/components/AgentAnalytics/components/AgentActivityChart';
import type { ManagerAgentAnalyticsItem } from 'services/api/getManagerAgentAnalytics';
import { useGetManagerAgentsAnalytics } from 'services/hooks/queries';
import { useUpdateAgentAvailabileStatusById } from 'services/hooks/mutations';
import { usePerformanceAgentsSort } from '../hooks/usePerformanceAgentsSort';
import { usePerformanceAgentsNameFilter } from '../hooks/usePerformanceAgentsNameFilter';
import { usePerformanceAgentsStatusFilter } from '../hooks/usePerformanceAgentsStatusFilter';
import { agentStatusChipColor, formatAgentStatus, formatMetricValue } from './PerformanceAnalyticsTab.utils';
import PerformanceSummaryMetrics from './PerformanceSummaryMetrics';
import QueueDelayAlertStrip from './QueueDelayAlertStrip';
import CapacityProjectionCard from './CapacityProjectionCard';
import { UserRoundX } from 'lucide-react';

import styles from '../ManagerCommandCenter.module.scss';

interface PerformanceAnalyticsTabProps {
    isManager: boolean;
}

const PerformanceAnalyticsTab: React.FC<PerformanceAnalyticsTabProps> = ({ isManager }) => {
    const { managerAgentsAnalytics, isManagerAgentsAnalyticsLoading } = useGetManagerAgentsAnalytics();
    const { updateAgentAvailabileStatusById } = useUpdateAgentAvailabileStatusById();

    const { activityByHour, agents } = managerAgentsAnalytics;
    const isActivityChartLoading =
        isManagerAgentsAnalyticsLoading && activityByHour.buckets.length === 0 && !activityByHour.timezone;

    const { filteredAgents: nameFilteredAgents, nameFilterHeader } = usePerformanceAgentsNameFilter(agents);
    const { filteredAgents: statusFilteredAgents, statusFilterHeader } =
        usePerformanceAgentsStatusFilter(nameFilteredAgents);
    const { sortedAgents: performanceTableRows, metricSortHeader } = usePerformanceAgentsSort(statusFilteredAgents);

    const performanceColumns = useMemo<TableColumn<ManagerAgentAnalyticsItem>[]>(
        () => [
            {
                key: 'name',
                title: nameFilterHeader(),
                width: 250,
                dataIndex: 'name',
            },
            {
                key: 'status',
                title: statusFilterHeader(),
                render: (_, row) => (
                    <div className={styles.statusCell}>
                        <Chip
                            variant='filled'
                            color={agentStatusChipColor(row.status)}
                            size='md'
                            className={styles.statusChip}
                        >
                            {formatAgentStatus(row.status)}
                        </Chip>
                        {isManager && row.status === 'online' ? (
                            <Tooltip content='Set offline'>
                                <Button
                                    variant='outline-primary'
                                    size='sm'
                                    iconOnly
                                    icon={UserRoundX}
                                    aria-label={`Set ${row.name} offline`}
                                    onClick={() => updateAgentAvailabileStatusById({ id: row.id, status: false })}
                                />
                            </Tooltip>
                        ) : null}
                    </div>
                ),
            },
            {
                key: 'transfers',
                title: metricSortHeader('Transferred calls', 'transfers'),
                render: (_, row) => formatMetricValue(row.agentPerformance.transfersCompletedToday, 'count'),
            },
            {
                key: 'transfer-response',
                title: metricSortHeader('Transfer response', 'transfer-response'),
                render: (_, row) => formatMetricValue(row.agentPerformance.transferResponseRate?.value, 'rate'),
            },
            {
                key: 'transfer-conversion',
                title: metricSortHeader('Transfer conversion', 'transfer-conversion'),
                render: (_, row) => formatMetricValue(row.agentPerformance.transferConversionRate?.value, 'rate'),
            },
            {
                key: 'callbacks',
                title: metricSortHeader('Callbacks taken', 'callbacks'),
                render: (_, row) => formatMetricValue(row.agentPerformance.callbacksHandled, 'count'),
            },
            {
                key: 'callback-response',
                title: metricSortHeader('Callback response', 'callback-response'),
                render: (_, row) => formatMetricValue(row.agentPerformance.callbackResponseRate?.value, 'rate'),
            },
            {
                key: 'callback-conversion',
                title: metricSortHeader('Callback conversion', 'callback-conversion'),
                render: (_, row) => formatMetricValue(row.agentPerformance.callbackConversionRate?.value, 'rate'),
            },
            {
                key: 'active-time',
                title: metricSortHeader('Active time', 'active-time'),
                render: (_, row) => formatMetricValue(row.operational.timeOnlineTodaySeconds, 'duration'),
            },
            {
                key: 'waiting',
                title: metricSortHeader('Waiting since transfer', 'waiting'),
                render: (_, row) => formatMetricValue(row.operational.waitingTimeSeconds, 'duration'),
            },
        ],
        [metricSortHeader, nameFilterHeader, statusFilterHeader, isManager, updateAgentAvailabileStatusById]
    );

    if (isManagerAgentsAnalyticsLoading && agents.length === 0) {
        return (
            <div className='flex flex-col gap-2xl'>
                <QueueDelayAlertStrip isManager={isManager} />
                <div className='loading'>
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    return (
        <div className='flex flex-col gap-2xl'>
            <QueueDelayAlertStrip isManager={isManager} />

            <PerformanceSummaryMetrics agents={agents} />

            <CapacityProjectionCard isManager={isManager} />

            <section className='flex flex-col gap-lg' aria-labelledby='activity-heading'>
                <div className='flex flex-col gap-xs'>
                    <h3 id='activity-heading'>Activity by hour</h3>
                    <p className='text-sm text-secondary'>Tenant call volume by hour today (all agents).</p>
                </div>
                <AgentActivityChart
                    activityByHour={activityByHour}
                    isLoading={isActivityChartLoading}
                    showUpdateLabel={false}
                />
            </section>

            <section className='flex flex-col gap-lg' aria-labelledby='agents-performance-heading'>
                <div className='flex flex-col gap-xs'>
                    <h3 id='agents-performance-heading'>Agent performance</h3>
                    <p className='text-sm text-secondary'>
                        Today’s metrics for all agents. Filter by agent or status, or click a metric column to sort.
                    </p>
                </div>

                <Table
                    isLoading={isManagerAgentsAnalyticsLoading}
                    data={performanceTableRows}
                    columns={performanceColumns}
                    rowKey='id'
                    hoverable
                    variant='secondary'
                    emptyText='No agents found for this tenant.'
                    className={styles.scrollableTable}
                />
            </section>
        </div>
    );
};

export default PerformanceAnalyticsTab;
